import * as React from "react";
import { Popover as PopoverPrimitive } from "radix-ui";

import { cn } from "../lib/cn";
import { useAiElementsScope } from "../scope";

export type PopoverProps = React.ComponentProps<typeof PopoverPrimitive.Root>;
export type PopoverTriggerProps = React.ComponentProps<typeof PopoverPrimitive.Trigger>;
export type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content>;
export type PopoverAnchorProps = React.ComponentProps<typeof PopoverPrimitive.Anchor>;

/** Scoped wrapper for the upstream popover root. */
export const Popover = (props: PopoverProps) => (
  <PopoverPrimitive.Root data-slot="popover" {...props} />
);
/** Scoped wrapper for the upstream popover trigger. */
export const PopoverTrigger = (props: PopoverTriggerProps) => (
  <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
);
/** Scoped wrapper for the upstream popover anchor. */
export const PopoverAnchor = (props: PopoverAnchorProps) => (
  <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
);
/** Popover content whose portal stays inside the AI Elements theme boundary. */
export const PopoverContent = ({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}: PopoverContentProps) => {
  const container = useAiElementsScope();
  return (
    <PopoverPrimitive.Portal container={container ?? undefined} data-slot="popover-portal">
      <PopoverPrimitive.Content
        align={align}
        className={cn(
          // The scoped portal can live under a `pointer-events-none` host frame (the dock), and a
          // non-modal Radix popover never re-enables pointer events itself, so the content must.
          "pointer-events-auto z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className,
        )}
        data-slot="popover-content"
        sideOffset={sideOffset}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
};

export type PopoverHeaderProps = React.ComponentProps<"div">;
/** Stacked title and description block for a popover. */
export const PopoverHeader = ({ className, ...props }: PopoverHeaderProps) => (
  <div
    className={cn("flex flex-col gap-1 text-sm", className)}
    data-slot="popover-header"
    {...props}
  />
);
export type PopoverTitleProps = React.ComponentProps<"div">;
/** Emphasized heading line inside a {@link PopoverHeader}. */
export const PopoverTitle = ({ className, ...props }: PopoverTitleProps) => (
  <div className={cn("font-medium", className)} data-slot="popover-title" {...props} />
);
export type PopoverDescriptionProps = React.ComponentProps<"p">;
/** Supporting copy inside a {@link PopoverHeader}. */
export const PopoverDescription = ({ className, ...props }: PopoverDescriptionProps) => (
  <p
    className={cn("text-muted-foreground", className)}
    data-slot="popover-description"
    {...props}
  />
);
